import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { ErrorState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import {
  assetCategoriesApi,
  assetLocationsApi,
  assetsApi,
} from '@/services/api/endpoints/assets';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import {
  ASSET_CONDITIONS,
  ASSET_STATUSES,
  type Asset,
  type AssetCondition,
  type AssetInput,
  type AssetStatus,
} from '@/types/asset';
import {
  ASSET_STATUS_TONE,
  AssetConditionBadge,
  AssetStatusBadge,
  Stat,
  WarrantyBadge,
  humanise,
} from './shared';

/** Blank text becomes "not stated" rather than an empty string on the server. */
function optional(value: string): string | undefined {
  return value.trim() === '' ? undefined : value.trim();
}

/**
 * The asset register.
 *
 * <p>Searching, filtering and paging happen on the server; the search box
 * matches tag, name, serial and model. The strip across the top is the
 * server's dashboard — counts it took from the database, shown as they came.
 *
 * <p>Registering captures the descriptive facts plus, once only, where the
 * asset starts and the condition it arrives in. From then on location,
 * custody, condition and status change only through the named operations on
 * the asset's own page, each of which writes its history.
 */
export function AssetsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const table = useTableQuery('assetTag,asc');
  const manages = can(PERMISSIONS.ASSET_MANAGE);

  const [statusFilter, setStatusFilter] = useState<AssetStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const [registering, setRegistering] = useState(false);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [vendor, setVendor] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('');
  const [warrantyExpiresOn, setWarrantyExpiresOn] = useState('');
  const [locationId, setLocationId] = useState('');
  const [condition, setCondition] = useState<AssetCondition | ''>('');
  const [notes, setNotes] = useState('');

  const params = {
    ...table.params,
    ...(statusFilter === '' ? {} : { status: statusFilter }),
    ...(categoryFilter === '' ? {} : { categoryId: categoryFilter }),
    ...(locationFilter === '' ? {} : { locationId: locationFilter }),
  };

  const assets = useQuery({
    queryKey: queryKeys.assets.list(params),
    queryFn: () => assetsApi.list(params),
  });

  const dashboard = useQuery({
    queryKey: queryKeys.assets.dashboard,
    queryFn: () => assetsApi.dashboard(),
  });

  const categories = useQuery({
    queryKey: queryKeys.assetReference.categories,
    queryFn: () => assetCategoriesApi.list(),
  });

  const locations = useQuery({
    queryKey: queryKeys.assetReference.locationsAll,
    queryFn: () => assetLocationsApi.list(),
  });

  // Tagged assets belong to durable categories; consumables are counted as stock.
  const registrableCategories = (categories.data ?? []).filter(
    (row) => row.status === 'ACTIVE' && !row.consumable,
  );
  const activeLocations = (locations.data ?? []).filter((row) => row.status === 'ACTIVE');

  function openRegister() {
    setName('');
    setCategoryId('');
    setSerialNumber('');
    setManufacturer('');
    setModel('');
    setVendor('');
    setPurchaseDate('');
    setPurchaseCost('');
    setWarrantyExpiresOn('');
    setLocationId('');
    setCondition('');
    setNotes('');
    setRegistering(true);
  }

  const register = useMutation({
    mutationFn: () => {
      const payload: AssetInput = {
        name: name.trim(),
        categoryId,
        serialNumber: optional(serialNumber),
        manufacturer: optional(manufacturer),
        model: optional(model),
        vendor: optional(vendor),
        purchaseDate: purchaseDate === '' ? undefined : purchaseDate,
        purchaseCost: purchaseCost.trim() === '' ? undefined : Number(purchaseCost),
        warrantyExpiresOn: warrantyExpiresOn === '' ? undefined : warrantyExpiresOn,
        locationId: locationId === '' ? undefined : locationId,
        condition: condition === '' ? undefined : condition,
        notes: optional(notes),
      };
      return assetsApi.register(payload);
    },
    onSuccess: (asset) => {
      toast.success(`${asset.assetTag} registered`);
      setRegistering(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.assetReference.all });
      navigate(ROUTES.assetDetail(asset.id));
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Asset>[] = [
    {
      key: 'asset',
      header: 'Asset',
      sortKey: 'assetTag',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.assetDetail(row.id)}
            className="block text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.name}
          </Link>
          <span className="block font-mono text-[11px] text-subtle">{row.assetTag}</span>
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.categoryName ?? '—'}</span>
          <span className="block text-[11px] text-subtle">
            {row.manufacturer === undefined && row.model === undefined
              ? '—'
              : [row.manufacturer, row.model].filter((part) => part !== undefined).join(' ')}
          </span>
        </span>
      ),
    },
    {
      key: 'where',
      header: 'Where / who',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.locationName ?? 'No location'}</span>
          <span className="block text-[11px] text-subtle">
            {row.custodianEmployeeName ?? row.custodianDepartmentName ?? 'Unassigned'}
          </span>
        </span>
      ),
    },
    {
      key: 'condition',
      header: 'Condition',
      sortKey: 'condition',
      render: (row) => <AssetConditionBadge condition={row.condition} />,
    },
    {
      key: 'warranty',
      header: 'Warranty',
      render: (row) => <WarrantyBadge state={row.warrantyState} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (row) => <AssetStatusBadge status={row.status} />,
    },
  ];

  const filtered = statusFilter !== '' || categoryFilter !== '' || locationFilter !== '';
  const stats = dashboard.data;

  return (
    <>
      <div className="mb-4">
        {dashboard.isError ? (
          <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
            {ASSET_STATUSES.map((status) => (
              <Stat
                key={status}
                label={humanise(status)}
                tone={ASSET_STATUS_TONE[status]}
                value={stats === undefined ? '…' : (stats.assetsByStatus[status] ?? 0)}
              />
            ))}
            <Stat
              label="Warranties expiring"
              tone={stats !== undefined && stats.warrantiesExpiringIn30Days > 0 ? 'warning' : 'neutral'}
              value={stats === undefined ? '…' : stats.warrantiesExpiringIn30Days}
              hint="Within 30 days"
            />
            <Stat
              label="Low stock"
              tone={stats !== undefined && stats.stockItemsAtOrBelowReorder > 0 ? 'warning' : 'neutral'}
              value={stats === undefined ? '…' : stats.stockItemsAtOrBelowReorder}
              hint="At or below reorder"
            />
            <Stat
              label="Open counts"
              value={stats === undefined ? '…' : stats.openAudits}
            />
          </div>
        )}
      </div>

      <ListPage
        title="Assets"
        description="The register: every tagged item, where it is, who has it and what shape it is in."
        icon={<Package className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={openRegister}>Register asset</Button> : undefined}
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Tag, name, serial, model…',
        }}
        filters={
          <>
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={ASSET_STATUSES.map((value) => ({ value, label: humanise(value) }))}
            />
            <FilterSelect
              label="Category"
              value={categoryFilter}
              onChange={(value) => {
                setCategoryFilter(value);
                table.setPage(0);
              }}
              options={(categories.data ?? [])
                .filter((row) => !row.consumable)
                .map((row) => ({ value: row.id, label: row.name }))}
            />
            <FilterSelect
              label="Location"
              value={locationFilter}
              onChange={(value) => {
                setLocationFilter(value);
                table.setPage(0);
              }}
              options={(locations.data ?? []).map((row) => ({
                value: row.id,
                label: row.campusName === undefined ? row.name : `${row.name} · ${row.campusName}`,
              }))}
            />
          </>
        }
        pagination={assets.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={assets.data?.data}
          rowKey={(row) => row.id}
          loading={assets.isPending}
          error={assets.error}
          onRetry={() => void assets.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle={table.searchInput === '' && !filtered ? 'No assets yet' : 'Nothing matched'}
          emptyDescription={
            table.searchInput === '' && !filtered
              ? 'Register the first asset to start the register.'
              : 'The search matches tag, name, serial and model.'
          }
        />
      </ListPage>

      <Modal
        open={registering}
        onClose={() => setRegistering(false)}
        title="Register asset"
        description="The tag is issued by the school's counter. Location and condition are set here once; afterwards they change only through the asset's operations."
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setRegistering(false)}
              disabled={register.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => register.mutate()}
              loading={register.isPending}
              disabled={name.trim() === '' || categoryId === ''}
            >
              Register
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={200}
            required
          />
          <SelectField
            label="Category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            required
            hint={
              categories.isError
                ? 'Categories could not be loaded.'
                : registrableCategories.length === 0 && !categories.isPending
                  ? 'No active non-consumable categories — add one under Asset Setup.'
                  : 'Consumable categories are counted as stock, not tagged.'
            }
          >
            <option value="">{categories.isPending ? 'Loading…' : 'Choose a category'}</option>
            {registrableCategories.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </SelectField>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Serial number"
              value={serialNumber}
              onChange={(event) => setSerialNumber(event.target.value)}
              maxLength={100}
            />
            <TextField
              label="Vendor"
              value={vendor}
              onChange={(event) => setVendor(event.target.value)}
              maxLength={200}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Manufacturer"
              value={manufacturer}
              onChange={(event) => setManufacturer(event.target.value)}
              maxLength={150}
            />
            <TextField
              label="Model"
              value={model}
              onChange={(event) => setModel(event.target.value)}
              maxLength={150}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <TextField
              label="Purchased on"
              type="date"
              value={purchaseDate}
              onChange={(event) => setPurchaseDate(event.target.value)}
            />
            <TextField
              label="Purchase cost"
              type="number"
              min={0}
              step="0.01"
              value={purchaseCost}
              onChange={(event) => setPurchaseCost(event.target.value)}
            />
            <TextField
              label="Warranty until"
              type="date"
              value={warrantyExpiresOn}
              onChange={(event) => setWarrantyExpiresOn(event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Starting location"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Not placed yet</option>
              {activeLocations.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.campusName === undefined ? row.name : `${row.name} · ${row.campusName}`}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Condition on arrival"
              value={condition}
              onChange={(event) => setCondition(event.target.value as AssetCondition | '')}
              hint="Left blank, the server records it as new."
            >
              <option value="">Server default</option>
              {ASSET_CONDITIONS.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>
          </div>
          <TextField
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={1000}
          />
        </div>
      </Modal>
    </>
  );
}
